// src/components/booking/AgendarForm.tsx
// Formulario INLINE de agendamiento para el link general /agendar (sin vehículo).
// Reemplaza al modal-wizard con un form plano: todo a la vista, scroll natural de
// página. Reutiliza las piezas de BookingModal (CalendarGrid, useMonthAvailability,
// helpers de bookingShared) para no duplicar lógica. Pensado para migrar luego a un
// bloque personalizable del builder.
'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { Button, Chip, Spinner, Select, SelectItem } from '@heroui/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { supabase } from '@/lib/supabase';
import useCustomerStore from '@/store/useCustomerStore';
import useClientStore from '@/store/useClientStore';
import type { Client } from '@/utils/types';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { tDelTenant } from '@/i18n/delTenant';
import { horaDelPais } from '@/lib/country';
import { sendEmail, createAppointmentEmailTemplate } from '@/lib/send-email';
import { sendSiteFormEmail, idNumerico } from '@/lib/site-form-email';
import { CalendarGrid } from './CalendarGrid';
import TimelineStepper from './TimelineStepper';
import { useMonthAvailability } from './useMonthAvailability';
import AppointmentsManagerModal from './AppointmentsManagerModal';
import { loadBookingDealerships } from './loadBookingDealerships';
import { loadAvailability, loadOccupancy, bookSlot } from './loadAvailability';
import {
  pickAccentFromClient,
  hexToRgb,
  rgba,
  labelFromTimestamp,
  minuteKey,
  prettyYMD,
  isFutureSlotSameDay,
  slotStartLabel,
  slotEndLabel,
  slotKeyOf,
  slotIsFuture,
  type SlotUtc,
  businessTimezone,
  isEmail,
  isNonEmpty,
  cleanPhone,
  isNumeric,
  VISIT_REASONS,
  serviceNameLabel,
  bookingDateLocale,
  fill,
  quedoPorAceptar,
  type BookedRow,
  type Slot,
  type DealershipItem,
} from './bookingShared';

const NOTE_MAX = 300;

interface AgendarFormProps {
  /** OPCIONAL: /agendar lo pasa; el embed del builder puede omitirlo y caer al store. */
  client?: Client;
  /** Uso dentro del card de un bloque del builder: sin chrome de página completa. */
  embedded?: boolean;
  /** Paleta heredada del nodo Craft.js (aceptadas para no desalinear el contrato). */
  bgColor?: string;
  textColor?: string;
  /** Overridea el color de marca del botón/acentos. */
  accentColor?: string;
}

/** Estado de la confirmación inline tras agendar con éxito. */
type Confirmation = {
  dateLabel: string;
  timeLabel: string;
  branchLabel: string;
  reason: string;
  /**
   * #228: la cita quedó esperando que la automotora la acepte. El panel cuenta
   * otra cosa — "pediste" en vez de "te esperamos".
   */
  porAceptar: boolean;
};

export default function AgendarForm({
  client: clientProp,
  embedded = false,
  bgColor,
  textColor,
  accentColor,
}: AgendarFormProps = {}) {
  const { customer, initializeCustomer, appointmentTokens, addAppointmentToken } = useCustomerStore();
  // Fallback al store cuando el embed no pasa el tenant (mismo store que usa /agendar).
  const { client: clientFromStore } = useClientStore();
  const client = clientProp ?? clientFromStore;
  // Textos por i18n (en español, idénticos a los de siempre; Brasil en pt) y
  // fechas por PAÍS: 'pt-BR' en Brasil, 'en-US' en EE.UU., `undefined` (= lo de
  // siempre) en el resto. La hora, 'HH:mm' como siempre; EE.UU. "2:30 PM".
  const { t } = useTranslation();
  const dateLocale = bookingDateLocale(client?.country);
  const hora = (hhmm: string) => horaDelPais(hhmm, client?.country);

  // Mismo origen de color que el botón "Agendar" / el modal.
  // accentColor (del builder) tiene prioridad; pickAccentFromClient tolera undefined.
  const brandHex = accentColor ?? pickAccentFromClient(client) ?? '#0ea5e9';
  const brandRGB = useMemo(() => hexToRgb(brandHex), [brandHex]);

  // estado
  const [dealerships, setDealerships] = useState<DealershipItem[]>([]);
  const [selectedDealershipId, setSelectedDealershipId] = useState<number | null>(null);
  /* Wizard de 4 pasos. El estado vive ACA, como todo el resto del form: los
     pasos solo deciden que se muestra, no mueven la logica de reserva a hijos. */
  const [step, setStep] = useState(1);
  const [monthDate, setMonthDate] = useState<Date>(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slots, setSlots] = useState<SlotUtc[]>([]);
  const [bookedByStart, setBookedByStart] = useState<Record<string, number>>({});
  const [selectedSlot, setSelectedSlot] = useState<SlotUtc | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<boolean>(() => !customer);
  const [firstName, setFirstName] = useState<string>(customer?.first_name || '');
  const [lastName, setLastName] = useState<string>(customer?.last_name || '');
  const [email, setEmail] = useState<string>(customer?.email || '');
  const [phone, setPhone] = useState<string>(customer?.phone || '');
  const [userNote, setUserNote] = useState<string>('');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  const formValid = isNonEmpty(firstName) && isNonEmpty(lastName) && isEmail(email) && cleanPhone(phone).length >= 8;

  /* sucursales */
  useEffect(() => {
    const cargar = async () => {
      if (!client?.id) return;
      const list = await loadBookingDealerships(Number(client.id));
      setDealerships(list);
      // Una sola sucursal → autoseleccionar; el selector no se muestra.
      if (list.length === 1) setSelectedDealershipId(list[0].id);
    };
    cargar();
  }, [client?.id]);

  const selectedDealership = useMemo(
    () => dealerships.find((d) => d.id === selectedDealershipId) ?? null,
    [dealerships, selectedDealershipId],
  );

  // La zona de la SUCURSAL, no la del navegador del visitante. Ver
  // `businessTimezone` en bookingShared.ts: con la zona del visitante, una
  // automotora chilena vista desde Madrid o Tokio recibía la grilla del día
  // anterior y la cita se guardaba corrida un día.
  const tz = useMemo(
    () => businessTimezone(client, selectedDealership),
    [client, selectedDealership],
  );

  /* disponibilidad mensual */
  const { loading: monthLoading, availableDays } = useMonthAvailability({
    clientId: client?.id ? Number(client.id) : undefined,
    dealershipId: selectedDealershipId,
    monthDate, tz,
  });

  /* slots por fecha (misma lógica que fetchSlots del modal) */
  const fetchSlots = useCallback(async () => {
    setSelectedSlot(null); setSlots([]); setBookedByStart({});
    if (!client?.id || !selectedDealershipId || !selectedDate) return;
    setLoadingSlots(true);
    try {
      // Pide INSTANTES; si la base no tiene las RPC `_utc` todavía, cae sola al
      // reloj de pared de siempre. Ver loadAvailability.ts.
      // Sin memoria: acá el visitante elige la hora, y la lista tiene que ser
      // la de la base en este instante (ver OpcionesDisponibilidad).
      const { slots: filas, utc } = await loadAvailability(
        Number(client.id), Number(selectedDealershipId), selectedDate, tz,
        { sinMemoria: true },
      );
      setLoadingSlots(false);
      setSlots(filas);
      setBookedByStart(await loadOccupancy(
        Number(client.id), Number(selectedDealershipId), selectedDate, tz, utc,
      ));
    } catch (e) {
      setLoadingSlots(false);
      toast.error(fill(t('booking.toast.slotsLoadError'), { error: String((e as { message?: string })?.message || e) }));
    }
  }, [client?.id, selectedDealershipId, selectedDate, tz]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  /* sync store → form */
  useEffect(() => {
    if (customer) {
      setFirstName(customer.first_name || '');
      setLastName(customer.last_name || '');
      setEmail(customer.email || '');
      setPhone(customer.phone || '');
      setEditingCustomer(false);
    }
  }, [customer]);

  const visibleSlots = (slots || [])
    .filter((s) => selectedDate && slotIsFuture(s, selectedDate, tz))
    .filter((s) => {
      const key = slotKeyOf(s);
      const taken = bookedByStart[key] || 0;
      const capacity = s.capacity ?? 1;
      return capacity - taken > 0;
    });

  /* helpers de customer (idénticos al modal) */
  const saveCustomer = async () => {
    if (!client?.id) return toast.error(t('booking.toast.missingClientId'));
    if (!formValid) return toast.error(t('booking.toast.completeData'));
    try {
      await initializeCustomer({
        first_name: firstName.trim(), last_name: lastName.trim(),
        email: email.trim().toLowerCase(), phone: cleanPhone(phone),
        client_id: Number(client.id),
      } as any);
      toast.success(t('booking.toast.dataSaved')); setEditingCustomer(false);
    } catch (e: any) { toast.error(e?.message || t('booking.toast.dataSaveFailed')); }
  };

  const ensureCustomer = async (): Promise<boolean> => {
    if ((customer as any)?.id || (customer as any)?.uuid) return true;
    if (!formValid) { toast.info(t('booking.toast.completeToContinue')); setEditingCustomer(true); return false; }
    try {
      const saved = await initializeCustomer({
        first_name: firstName.trim(), last_name: lastName.trim(),
        email: email.trim().toLowerCase(), phone: cleanPhone(phone),
        client_id: Number(client?.id),
      } as any);
      return !!saved;
    } catch (e: any) { toast.error(e?.message || t('booking.toast.dataSaveFailed')); return false; }
  };

  /* emails: reutiliza la plantilla compartida (createAppointmentEmailTemplate).
     Modo general (sin vehículo): asunto por motivo, sin marca/modelo. */
  const sendAppointmentEmails = async (appointmentData: {
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    appointmentDate: string;
    appointmentTime: string;
    dealershipAddress: string;
    notes?: string;
    /**
     * #228: la cita quedó esperando que la automotora la acepte. Cambia el
     * encabezado del correo y los dos asuntos, no el detalle.
     */
    pendingApproval?: boolean;
  }) => {
    try {
      // `country`: la plantilla sale en portugués para Brasil y la de siempre
      // para el resto. Va en una variable (no literal) para no depender del
      // tipo exacto de la plantilla.
      const datosCorreo = {
        ...appointmentData,
        vehicleDetails: { brand: '', model: '', year: '' },
        country: client?.country ?? null,
      };
      const emailContent = createAppointmentEmailTemplate(datosCorreo);

      // Los dos asuntos van en el idioma del TENANT, no en el del visitante: el
      // cuerpo del correo (createAppointmentEmailTemplate) sale por país, y un
      // asunto en inglés sobre un cuerpo en español queda mezclado.
      const tAutomotora = tDelTenant(client?.country);
      const reasonLabel = serviceNameLabel(selectedReason || 'visita', tAutomotora);
      // #228: por aceptar, los dos asuntos lo dicen. Al cliente, que es una
      // solicitud y no una confirmación; a la automotora, que hay algo que
      // responder — es el correo que evita que la cita se pierda, porque para
      // Concecars los avisos de la base están apagados y éste es el único que
      // sale.
      const porAceptar = !!appointmentData.pendingApproval;
      const dealershipSubject = fill(
        tAutomotora(porAceptar ? 'booking.email.newPendingSubject' : 'booking.email.newSubject'),
        { reason: reasonLabel },
      );

      // El correo al cliente lo manda el backend (outbox `appointment_communications`),
      // según el aviso de cada sucursal. Desde el navegador ya no se puede: send-email
      // no acepta envíos sin sesión.

      // 2. Notificación a la automotora.
      const dealershipEmails = client?.contact?.email ? [client.contact.email] : [];
      //    Destinatario/asunto/HTML los arma la edge (`site_form`); si no
      //    puede, cae a este mismo envío de siempre.
      if (dealershipEmails.length > 0) {
        await sendSiteFormEmail(
          {
            client_id: idNumerico(client?.id) ?? 0,
            kind: 'booking_dealer',
            vehicle_id: null,
            params: {
              ...datosCorreo,
              // Insumos del asunto (booking.email.newSubject con {reason}).
              reason: selectedReason || 'visita',
              reasonLabel,
              withVehicle: false,
              vehicleLabel: '',
            },
          },
          {
            to: dealershipEmails,
            subject: dealershipSubject,
            content: emailContent,
          },
          sendEmail
        );
      }
    } catch (error) {
      console.error('Error al enviar emails de cita:', error);
    }
  };

  const canConfirm =
    !!selectedDealershipId && !!selectedDate && !!selectedSlot && !!selectedReason &&
    !!client?.id && (!!customer || formValid);

  /* Que hace falta para poder avanzar DESDE cada paso. Se arma con los mismos
     flags que ya usaba `canConfirm`: una sola fuente de verdad, para que el
     boton Continuar no pueda desalinearse del boton Agendar. */
  const STEPS = [t('booking.common.branch'), t('booking.form.stepDayTime'), t('booking.form.stepReasonData'), t('booking.form.stepConfirm')];
  const canNext =
    step === 1 ? !!selectedDealershipId :
    step === 2 ? (!!selectedDate && !!selectedSlot) :
    step === 3 ? (!!selectedReason && (!!customer || formValid)) :
    false;

  const confirmAppointment = async () => {
    if (!client?.id || !selectedDealershipId || !selectedSlot || !selectedDate || !selectedReason) return;
    setSubmitting(true);
    try {
      const ok = await ensureCustomer();
      if (!ok) return;

      // Customer fresco del store: ensureCustomer puede haberlo creado recién.
      const freshCustomer = useCustomerStore.getState().customer as any;
      const rawCid = freshCustomer?.id ?? freshCustomer?.customer_id;
      const cidBigint: number | null = isNumeric(rawCid) ? Number(rawCid) : null;

      const contactName = `${(customer?.first_name || firstName).trim()} ${(customer?.last_name || lastName).trim()}`.trim();
      const contactEmail = (customer?.email || email).trim().toLowerCase();
      const contactPhone = (customer?.phone || phone).trim();
      const finalNotes = userNote.trim() ? userNote.trim().slice(0, NOTE_MAX) : null;

      const branchLabel = dealerships.find(d => d.id === selectedDealershipId)?.address || fill(t('booking.common.branchNumber'), { id: String(selectedDealershipId) });

      const { data, error } = await bookSlot({
        clientId: Number(client.id),
        dealershipId: Number(selectedDealershipId),
        serviceName: selectedReason || 'visita',
        slot: selectedSlot,
        ymd: selectedDate,
        tz,
        customerId: cidBigint != null ? Number(cidBigint) : null,
        channel: 'web',
        notes: finalNotes,
        contact: { name: contactName, email: contactEmail, phone: contactPhone },
      });

      if (error) {
        const code = error.message || '';
        switch (code) {
          case 'slot_taken':
            toast.error(t('booking.toast.slotTaken'));
            fetchSlots();
            break;
          case 'already_booked':
            toast.error(t('booking.toast.alreadyBooked'));
            break;
          case 'too_many_appointments':
            toast.error(t('booking.toast.tooMany'));
            break;
          case 'slot_not_available':
          case 'slot_in_past':
            toast.error(t('booking.toast.slotUnavailable'));
            fetchSlots();
            break;
          default:
            toast.error(fill(t('booking.toast.createFailed'), { code }));
        }
        return;
      }

      // RETURNS TABLE → PostgREST entrega un array de filas.
      const row = (Array.isArray(data) ? data[0] : data) as BookedRow | undefined;
      if (row?.cancel_token) addAppointmentToken(String(row.cancel_token));

      // #228. Lo decide la BASE, no la pantalla: la sucursal pudo encender o
      // apagar el interruptor entre que se cargó el formulario y que se apretó
      // "Agendar", así que el único dato confiable es el estado que volvió.
      const porAceptar = quedoPorAceptar(row);

      const timeLabel = `${hora(slotStartLabel(selectedSlot, tz))} – ${hora(slotEndLabel(selectedSlot, tz))}`;
      const dateLabel = prettyYMD(selectedDate, dateLocale);

      await sendAppointmentEmails({
        pendingApproval: porAceptar,
        customerName: contactName,
        customerEmail: contactEmail,
        customerPhone: contactPhone,
        appointmentDate: dateLabel,
        appointmentTime: timeLabel,
        // El correo lo lee también la automotora: la sucursal sin dirección va
        // en el idioma del TENANT (la confirmación en pantalla, en el del visitante).
        dealershipAddress: dealerships.find(d => d.id === selectedDealershipId)?.address || fill(tDelTenant(client?.country)('booking.common.branchNumber'), { id: String(selectedDealershipId) }),
        notes: finalNotes || undefined,
      });

      toast.success(t(porAceptar ? 'booking.toast.requested' : 'booking.toast.booked'));
      setConfirmation({ dateLabel, timeLabel, branchLabel, reason: selectedReason, porAceptar });
      // Scroll al tope para que se vea el panel de confirmación.
      // En modo embedded el bloque vive dentro de una página; no secuestramos el scroll global.
      if (!embedded && typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  /* Resetea el form para agendar otra visita (conserva el customer). */
  const resetForm = () => {
    setStep(1);
    setConfirmation(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setSelectedReason('');
    setUserNote('');
    setSlots([]);
    setBookedByStart({});
  };

  const hasSingleBranch = dealerships.length === 1;
  const manyBranches = dealerships.length >= 7;

  /* ========================= a prueba de balas =========================
     Dentro del builder, el BuilderErrorBoundary envuelve la PÁGINA ENTERA:
     un throw acá dejaría la web del tenant en blanco, no solo el bloque.
     Si el tenant aún no hidrató (store vacío en el primer paint) o el host
     no resuelve automotora, renderizamos un placeholder discreto y salimos.
     Va DESPUÉS de todos los hooks para no romper el orden de hooks. */
  if (!client) {
    return embedded ? (
      <div className="animate-pulse space-y-4" aria-hidden>
        <div className="h-10 rounded-md bg-gray-200 dark:bg-neutral-800" />
        <div className="h-48 rounded-md bg-gray-200 dark:bg-neutral-800" />
        <div className="h-12 rounded-md bg-gray-200 dark:bg-neutral-800" />
      </div>
    ) : (
      <div className="min-h-screen grid place-items-center bg-white dark:bg-dark-bg">
        <Spinner size="lg" />
      </div>
    );
  }

  /* ========================= confirmación inline ========================= */
  if (confirmation) {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-white dark:bg-dark-bg px-4 sm:px-6 py-12 sm:py-20'}>
        <div className={embedded ? '' : 'mx-auto w-full max-w-xl'}>
          <div className={embedded ? 'text-center' : 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-900/60 shadow-sm p-6 sm:p-8 text-center'}>
            <div
              className="mx-auto mb-4 grid place-items-center rounded-full"
              style={{ width: 64, height: 64, backgroundColor: rgba(brandRGB, 0.12) }}
            >
              {/* #228: por aceptar, el ícono es un reloj y no un visto. El
                  visto dice "listo"; esto todavía no está listo. */}
              <Icon
                icon={confirmation.porAceptar ? 'mdi:calendar-clock' : 'mdi:calendar-check'}
                style={{ color: brandHex }}
                className="text-[36px]"
              />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2">
              {t(confirmation.porAceptar ? 'booking.form.doneRequested' : 'booking.form.done')}
            </h2>
            {/* La frase cambia entera, no una palabra: "te esperamos el X" es
                una promesa que la automotora todavía no hizo. Por aceptar se
                cuenta qué se pidió, y abajo, que falta la confirmación. */}
            <p className="text-gray-600 dark:text-gray-300">
              {t(confirmation.porAceptar ? 'booking.form.requestedOn' : 'booking.form.expectOn')}
              <b>{confirmation.dateLabel}</b>{t('booking.form.expectAt')}<b>{confirmation.timeLabel}</b>{t('booking.form.expectIn')}{' '}
              <b>{confirmation.branchLabel}</b>.
            </p>
            {confirmation.porAceptar && (
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-2">
                {t('booking.form.requestedNote')}
              </p>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {t('booking.form.reasonPrefix')}{serviceNameLabel(confirmation.reason, t)}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-6">
              <Button
                className="h-12 px-6 font-semibold text-white"
                style={{ backgroundColor: brandHex }}
                startContent={<Icon icon="mdi:calendar-plus" className="text-[20px]" />}
                onPress={resetForm}
              >
                {t('booking.common.bookAnother')}
              </Button>
              {appointmentTokens.length > 0 && (
                <Button
                  variant="flat"
                  className="h-12 px-6"
                  style={{ backgroundColor: rgba(brandRGB, 0.12), color: brandHex }}
                  startContent={<Icon icon="mdi:calendar-month-outline" className="text-[20px]" />}
                  onPress={() => setManageOpen(true)}
                >
                  {t('booking.common.viewMyAppointments')}
                </Button>
              )}
            </div>
          </div>
        </div>
        <AppointmentsManagerModal open={manageOpen} onOpenChange={setManageOpen} client={client} />
      </div>
    );
  }

  /* ========================= formulario ========================= */
  return (
    <div className={embedded ? '' : 'min-h-screen bg-white dark:bg-dark-bg px-4 sm:px-6 py-10 sm:py-16'}>
      <div className={embedded ? '' : 'mx-auto w-full max-w-2xl'}>
        {/* encabezado — solo en /agendar; en el embed el título/subtítulo los pinta el wrapper */}
        {!embedded && (
        <div className="text-center mb-8">
          {client?.logo ? (
            <Image
              src={client.logo}
              alt={client?.name || 'Logo'}
              width={180}
              height={72}
              className="mx-auto mb-6 h-14 w-auto object-contain"
              unoptimized
            />
          ) : null}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white mb-2">
            {t('booking.form.title')}
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400">
            {t('booking.form.subtitleBefore')}{client?.name || t('booking.form.subtitleFallbackName')}{t('booking.form.subtitleAfter')}
          </p>
        </div>
        )}

        <div className={embedded ? 'space-y-6' : 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-900/50 shadow-sm p-4 sm:p-6 space-y-6'}>
          {/* citas existentes */}
          {(customer || appointmentTokens.length > 0) && (
            <Button
              size="md"
              className="w-full"
              style={{
                background: rgba(brandRGB, 0.14),
                color: brandHex,
                borderColor: rgba(brandRGB, 0.25),
              }}
              startContent={<Icon icon="mdi:calendar-month-outline" className="text-[18px]" />}
              endContent={<Icon icon="mdi:arrow-right" className="text-[18px]" />}
              onPress={() => setManageOpen(true)}
            >
              {t('booking.common.viewMyAppointments')}
            </Button>
          )}

          {/* Stepper: el mismo del modal por vehiculo, para que el recorrido se
              vea igual en las dos puertas de reserva. */}
          <TimelineStepper steps={STEPS} current={step} brandRGB={brandRGB} />

          {/* PASO 1 — Sucursal */}
          {step === 1 && (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Icon icon="mdi:store-marker-outline" className="text-[18px]" />
              {t('booking.common.branch')}
            </h2>
            {hasSingleBranch ? (
              <div
                className="rounded-lg border p-3 text-sm text-gray-800 dark:text-gray-200 flex items-center gap-2"
                style={{ backgroundColor: rgba(brandRGB, 0.06), borderColor: rgba(brandRGB, 0.2) }}
              >
                <Icon icon="mdi:map-marker" style={{ color: brandHex }} className="shrink-0" />
                {dealerships[0]?.address || fill(t('booking.common.branchNumber'), { id: String(dealerships[0]?.id) })}
              </div>
            ) : manyBranches ? (
              <Select
                aria-label={t('booking.common.selectBranchAria')}
                placeholder={t('booking.common.selectBranchPlaceholder')}
                selectedKeys={selectedDealershipId ? new Set([String(selectedDealershipId)]) : new Set([])}
                onSelectionChange={(keys) => {
                  const first = Array.from(keys)[0] as string | undefined;
                  setSelectedDealershipId(first ? Number(first) : null);
                  setSelectedDate(null);
                  setSelectedSlot(null);
                }}
                disallowEmptySelection
              >
                {dealerships.map((d) => (
                  <SelectItem key={`dlp-${d.id}`} textValue={d.address || fill(t('booking.common.branchNumber'), { id: String(d.id) })}>
                    {d.address || fill(t('booking.common.branchNumber'), { id: String(d.id) })}
                  </SelectItem>
                ))}
              </Select>
            ) : (
              <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
                {dealerships.map((d) => {
                  const active = selectedDealershipId === d.id;
                  const label = d.address || fill(t('booking.common.branchNumber'), { id: String(d.id) });
                  return (
                    <Button
                      key={`dlp-btn-${d.id}`}
                      variant={active ? 'flat' : 'bordered'}
                      className="h-12"
                      style={active ? { backgroundColor: rgba(brandRGB, 0.1), color: brandHex, borderColor: rgba(brandRGB, 0.3) } : undefined}
                      onPress={() => { setSelectedDealershipId(d.id); setSelectedDate(null); setSelectedSlot(null); }}
                      title={label}
                    >
                      <Icon icon="mdi:store-marker-outline" className="mr-1.5 shrink-0" />
                      <span className="truncate w-full text-left">{label}</span>
                    </Button>
                  );
                })}
              </div>
            )}
            {dealerships.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400">{t('booking.form.loadingBranches')}</div>
            )}
          </section>
          )}

          {/* PASO 2 — Dia y hora */}
          {step === 2 && (
          <>
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Icon icon="mdi:calendar-month-outline" className="text-[18px]" />
              {t('booking.form.chooseDay')}
            </h2>
            <CalendarGrid
              value={selectedDate}
              onChange={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
              monthDate={monthDate}
              setMonthDate={(d) => { setMonthDate(d); setSelectedDate(null); setSelectedSlot(null); }}
              availableDays={availableDays}
              loading={monthLoading}
              brandRGB={brandRGB}
              locale={dateLocale}
            />
            <div className="rounded-md text-xs sm:text-[13px] p-2 flex items-start gap-2 text-gray-700 dark:text-gray-300" style={{ backgroundColor: rgba(brandRGB, 0.08) }}>
              <Icon icon="mdi:lightbulb-on-outline" className="mt-0.5 shrink-0" />
              <div>{t('booking.common.grayDaysHint')}</div>
            </div>
          </section>

          {/* 3. Horarios */}
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Icon icon="mdi:clock-outline" className="text-[18px]" />
              {selectedDate ? fill(t('booking.common.slotsFor'), { date: prettyYMD(selectedDate, dateLocale) }) : t('booking.form.chooseSlot')}
            </h2>
            {(!selectedDate) ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">{t('booking.form.pickDateFirst')}</div>
            ) : loadingSlots ? (
              <div className="flex items-center justify-center py-8"><Spinner /></div>
            ) : visibleSlots.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">{t('booking.common.noSlots')}</div>
            ) : (
              <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))]">
                {visibleSlots.map((s, idx) => {
                  const isSelected = !!selectedSlot && selectedSlot.slot_start === s.slot_start && selectedSlot.slot_end === s.slot_end;
                  const style: React.CSSProperties = isSelected
                    ? { backgroundColor: rgba(brandRGB, 0.14), color: brandHex, borderColor: rgba(brandRGB, 0.4), boxShadow: `0 0 0 2px ${rgba(brandRGB, 0.18)} inset` }
                    : { backgroundColor: rgba(brandRGB, 0.06), color: brandHex, borderColor: rgba(brandRGB, 0.18) };
                  return (
                    <Button key={`${s.slot_start}-${idx}`} className="h-11 whitespace-nowrap" variant="flat" style={style}
                      onPress={() => setSelectedSlot(s)}>
                      {hora(slotStartLabel(s, tz))} – {hora(slotEndLabel(s, tz))}
                    </Button>
                  );
                })}
              </div>
            )}
          </section>
          </>
          )}

          {/* PASO 3 — Motivo y datos */}
          {step === 3 && (
          <>
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Icon icon="mdi:help-circle-outline" className="text-[18px]" />
              {t('booking.common.visitReason')}
            </h2>
            <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
              {VISIT_REASONS.map((reason) => {
                const active = selectedReason === reason;
                return (
                  <Button
                    key={reason}
                    variant={active ? 'flat' : 'bordered'}
                    className="h-11 justify-start"
                    style={active ? { backgroundColor: rgba(brandRGB, 0.12), color: brandHex, borderColor: rgba(brandRGB, 0.35) } : undefined}
                    onPress={() => setSelectedReason(reason)}
                    title={serviceNameLabel(reason, t)}
                  >
                    <span className="truncate w-full text-left">{serviceNameLabel(reason, t)}</span>
                  </Button>
                );
              })}
            </div>
          </section>

          {/* 5. Datos */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                <Icon icon="mdi:account-outline" className="text-[18px]" />
                {t('booking.common.yourData')}
              </h2>
              {customer && !editingCustomer ? <Button size="sm" variant="light" onPress={() => setEditingCustomer(true)}>{t('booking.common.edit')}</Button> : null}
            </div>

            {!customer || editingCustomer ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('booking.common.firstName')}><input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white px-3 text-sm w-full" placeholder={t('booking.common.firstNamePlaceholder')} autoComplete="given-name" /></Field>
                <Field label={t('booking.common.lastName')}><input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white px-3 text-sm w-full" placeholder={t('booking.common.lastNamePlaceholder')} autoComplete="family-name" /></Field>
                <Field label={t('booking.common.email')}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white px-3 text-sm w-full" placeholder={t('booking.common.emailPlaceholder')} autoComplete="email" inputMode="email" /></Field>
                <Field label={t('booking.common.phone')}><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white px-3 text-sm w-full" placeholder={t('booking.common.phonePlaceholder')} autoComplete="tel" inputMode="tel" /></Field>
                {customer && (
                  <div className="sm:col-span-2 flex justify-end">
                    <Button isDisabled={!formValid || !client?.id} onPress={saveCustomer}
                      className="bg-primary text-primary-foreground"
                      startContent={<Icon icon="mdi:content-save" className="text-[18px]" />}>
                      {t('booking.common.saveMyData')}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Chip variant="flat" className="gap-1.5 inline-flex items-center text-gray-800 dark:text-gray-200" startContent={<Icon icon="mdi:account" className="text-[18px]" />} style={{ backgroundColor: rgba(brandRGB, 0.1) }}>
                  {customer.first_name} {customer.last_name}
                </Chip>
                <Chip variant="flat" className="gap-1.5 inline-flex items-center text-gray-800 dark:text-gray-200" startContent={<Icon icon="mdi:email-outline" className="text-[18px]" />} style={{ backgroundColor: rgba(brandRGB, 0.1) }}>
                  {customer.email}
                </Chip>
                <Chip variant="flat" className="gap-1.5 inline-flex items-center text-gray-800 dark:text-gray-200" startContent={<Icon icon="mdi:phone" className="text-[18px]" />} style={{ backgroundColor: rgba(brandRGB, 0.1) }}>
                  {customer.phone}
                </Chip>
              </div>
            )}

            <Field label={<span className="inline-flex items-center gap-1.5"><Icon icon="mdi:note-edit-outline" className="text-[18px]" />{` ${t('booking.common.noteOptional')}`}</span>}>
              <textarea rows={3} value={userNote} onChange={(e) => setUserNote(e.target.value.slice(0, NOTE_MAX))}
                placeholder={t('booking.common.notePlaceholder')}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white px-3 py-2 text-sm resize-y w-full" />
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{userNote.length}/{NOTE_MAX}</div>
            </Field>
          </section>
          </>
          )}

          {/* PASO 4 — Resumen y confirmacion */}
          {step === 4 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Icon icon="mdi:clipboard-check-outline" className="text-[18px]" />
              {t('booking.form.reviewConfirm')}
            </h2>
            <div
              className="rounded-lg border p-3 space-y-2 text-sm text-gray-800 dark:text-gray-200"
              style={{ backgroundColor: rgba(brandRGB, 0.06), borderColor: rgba(brandRGB, 0.2) }}
            >
              <Resumen icon="mdi:store-marker-outline" label={t('booking.common.branch')}
                value={dealerships.find((d) => d.id === selectedDealershipId)?.address || fill(t('booking.common.branchNumber'), { id: String(selectedDealershipId) })} />
              <Resumen icon="mdi:calendar-month-outline" label={t('booking.form.summaryDay')}
                value={selectedDate ? prettyYMD(selectedDate, dateLocale) : '—'} />
              <Resumen icon="mdi:clock-outline" label={t('booking.form.summaryTime')}
                value={selectedSlot ? `${hora(slotStartLabel(selectedSlot, tz))} – ${hora(slotEndLabel(selectedSlot, tz))}` : '—'} />
              <Resumen icon="mdi:help-circle-outline" label={t('booking.form.summaryReason')} value={serviceNameLabel(selectedReason, t) || '—'} />
              <Resumen icon="mdi:account-outline" label={t('booking.form.summaryName')}
                value={customer ? `${customer.first_name} ${customer.last_name}` : `${firstName} ${lastName}`.trim() || '—'} />
              <Resumen icon="mdi:email-outline" label={t('booking.common.email')} value={customer?.email || email || '—'} />
              {(userNote || '').trim() ? (
                <Resumen icon="mdi:note-edit-outline" label={t('booking.form.summaryNote')} value={userNote.trim()} />
              ) : null}
            </div>
          </section>
          )}

          {/* Navegacion del wizard. El boton Agendar es EL MISMO de antes: misma
              guarda `canConfirm`, mismo handler, mismos errores traducidos. */}
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button
                variant="bordered"
                className="h-12 px-5"
                isDisabled={submitting}
                onPress={() => setStep((n) => Math.max(1, n - 1))}
                startContent={<Icon icon="mdi:arrow-left" className="text-[18px]" />}
              >
                {t('booking.form.back')}
              </Button>
            )}
            {step < 4 && (
              <Button
                isDisabled={!canNext}
                onPress={() => setStep((n) => Math.min(4, n + 1))}
                className="flex-1 h-12 text-base font-semibold text-white disabled:opacity-40"
                style={{ backgroundColor: brandHex }}
                endContent={<Icon icon="mdi:arrow-right" className="text-[20px]" />}
              >
                {t('booking.form.continue')}
              </Button>
            )}
          </div>

          {step === 4 && (
          <Button
            isDisabled={!canConfirm || submitting}
            isLoading={submitting}
            onPress={confirmAppointment}
            className="w-full h-14 text-base font-semibold text-white disabled:opacity-40"
            style={{ backgroundColor: brandHex }}
            startContent={!submitting ? <Icon icon="mdi:calendar-check" className="text-[22px]" /> : undefined}
          >
            {t('booking.common.bookVisit')}
          </Button>
          )}
        </div>
      </div>

      <AppointmentsManagerModal open={manageOpen} onOpenChange={setManageOpen} client={client} />
    </div>
  );
}

/* subcomponentes */
function Resumen({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <Icon icon={icon} className="text-[18px] mt-0.5 shrink-0 opacity-70" />
      <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}:</span>
      <span className="font-medium break-words">{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</label>
      {children}
    </div>
  );
}
